import type { AccountType, CoreUser, Profile } from '../types';

function trimmed(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function combinedName(...parts: unknown[]): string {
  return parts.map(trimmed).filter(Boolean).join(' ');
}

export function displayUserName(profile?: Profile | null, user?: CoreUser | null): string {
  const fromPassport = combinedName(profile?.first_name, profile?.last_name);
  if (fromPassport) return fromPassport;

  const fromCoreFull =
    trimmed(user?.fullName) ||
    trimmed(user?.full_name) ||
    trimmed(user?.name);
  if (fromCoreFull) return fromCoreFull;

  const fromCoreParts = combinedName(
    user?.firstName ?? user?.first_name,
    user?.lastName ?? user?.last_name,
  );
  if (fromCoreParts) return fromCoreParts;

  return user?.email || profile?.contact_email || 'User';
}

export function avatarInitial(label: string): string {
  const ch = (label || '?').trim().charAt(0);
  return ch ? ch.toUpperCase() : '?';
}

const PASSPORT_ROLES: AccountType[] = ['verifier', 'manager', 'employee'];

export function rolesFromClaims(claims: Record<string, unknown>): string[] {
  return Array.isArray(claims.roles)
    ? claims.roles.filter((role): role is string => typeof role === 'string')
    : [];
}

/** Passport roles on a Core token, highest privilege first. */
export function passportRolesFromClaims(roles: string[]): AccountType[] {
  const lower = new Set(roles.map(role => role.toLowerCase()));
  return PASSPORT_ROLES.filter(role => lower.has(role));
}

export function accountTypeFromRoles(roles: string[]): AccountType | null {
  return passportRolesFromClaims(roles)[0] ?? null;
}

/**
 * Keeps a saved role when Core still assigned it. Otherwise uses the
 * highest role on the token.
 */
export function resolveActiveRole(roles: string[], preferred?: string | null): AccountType | null {
  const available = passportRolesFromClaims(roles);
  if (available.length === 0) return null;
  const wanted = preferred?.toLowerCase();
  return available.find(role => role === wanted) ?? available[0];
}

export function roleLabel(accountType: AccountType): string {
  if (accountType === 'verifier') return 'Verifier';
  if (accountType === 'manager') return 'Manager';
  return 'Employee';
}

export function userEmail(profile?: Profile | null, user?: CoreUser | null): string | null {
  return user?.email || profile?.contact_email || null;
}
